import type { IframePoolOrderItem } from '../../../modules/api';
import { OpenedItemCard } from '../cards/OpenedItemCard';
import { EmptyState } from '../EmptyState';

export interface DropsTabProps {
  items: IframePoolOrderItem[];
  /** First-ever load of this feed — the only state that gets a skeleton. A
   *  reload behind an already-rendered tab must not, see useInfinitePagination. */
  initialLoading: boolean;
  loadingMore: boolean;
  reachedEnd: boolean;
  /** Sentinel ref from useInfinitePagination — attach to a node at the
   *  end of the grid to drive infinite scroll. */
  sentinelRef: (node: Element | null) => void;
  onBrowse: () => void;
  /** Tap on a card toggles its membership in the selection set. */
  onItemClick?: (item: IframePoolOrderItem) => void;
  /** Tap the Buyback pill → instant sell, no modal. */
  onItemSell?: (item: IframePoolOrderItem) => Promise<void> | void;
  /** Item ids currently in the selection set — drives per-card visuals. */
  selectedIds?: ReadonlySet<string>;
  /** Bulk-sell in flight — freeze the grid so the user can't tap a card
   *  (which would change the selection) or trigger a per-card sell while
   *  the inventory POST is still in the air. */
  selling?: boolean;
}

const SKELETON_COUNT = 4;

function CardSkeleton() {
  // Mobile mirrors OpenedItemCard's aspect-square photo + small chrome
  // (≈ aspect 5/6); desktop matches the fixed 460px tall card. Without
  // the breakpoint switch, h-[460px] makes the mobile tile feel like a
  // tower next to the real cards in adjacent rows.
  return (
    <div
      data-co-component="MyPacksSkeleton"
      className="
        aspect-[5/6] w-full rounded-[20px] animate-pulse
        @[600px]:aspect-auto @[600px]:h-[460px] @[600px]:rounded-[32px]
      "
      style={{
        background: 'var(--co-MyPacksSkeleton-fill, rgba(255,255,255,0.05))',
        borderStyle: 'solid',
        borderColor: 'var(--co-MyPacksSkeleton-borderColor, rgba(171,174,179,0.24))',
        borderWidth: 'var(--co-MyPacksSkeleton-borderWidth, 2px)',
      }}
    />
  );
}

/**
 * Drops-tab grid — opened pool_items rendered as a responsive grid of
 * glass-pedestal cards. The UI label of this tab is "Cards" per the
 * Figma 706:1683 redesign; the internal name stays `Drops` to match
 * the backend term and existing call sites.
 */
export function DropsTab({
  items,
  initialLoading,
  loadingMore,
  reachedEnd,
  sentinelRef,
  onBrowse,
  onItemClick,
  onItemSell,
  selectedIds,
  selling = false,
}: DropsTabProps) {
  if (initialLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 @[600px]:gap-4 @[600px]:grid-cols-2 @[900px]:grid-cols-3 @[1200px]:grid-cols-4">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState title="No Cards Found" cta="Buy Packs" onAction={onBrowse} />
    );
  }

  return (
    <>
      <div
        aria-busy={selling || undefined}
        className={
          'grid grid-cols-2 gap-3 @[600px]:gap-4 @[600px]:grid-cols-2 @[900px]:grid-cols-3 @[1200px]:grid-cols-4 ' +
          (selling
            ? 'pointer-events-none opacity-60 transition-opacity'
            : 'transition-opacity')
        }
      >
        {items.map((item) => (
          <OpenedItemCard
            key={item.id}
            item={item}
            selected={selectedIds?.has(item.id) ?? false}
            {...(onItemClick ? { onClick: onItemClick } : {})}
            {...(onItemSell ? { onSell: onItemSell } : {})}
          />
        ))}
        {loadingMore &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <CardSkeleton key={`sk-${i}`} />
          ))}
      </div>
      {!reachedEnd && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </>
  );
}
